import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { AyvensQuestion } from '../components/ayvens/AyvensQuestion';
import { Car, Euro, Calendar, Gauge, Phone, Mail, User, MessageSquare, ChevronRight, Sparkles, Shield, CheckCircle, CheckCircle2, Loader2, ExternalLink, FileText, MapPin, Building2, Briefcase, Clock, AlertTriangle, Send } from 'lucide-react';
import { TIPOLOGIE_CLIENTE } from '../lib/tipologieCliente';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Logo component - Solo logo senza testo ridondante
const Logo = () => (
  <div className="flex items-center gap-3">
    <img 
      src="/assets/logos/logo-dark.png?v=2026" 
      alt="MAcheNoleggio - MAAC FINANCE SRL" 
      className="h-14 w-auto"
      onError={(e) => {
        e.target.style.display = 'none';
      }}
    />
    <div className="hidden sm:block">
      <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">MAAC FINANCE SRL</p>
    </div>
  </div>
);

// Hero Image per card Vetrina (usa endpoint pubblico, no auth, escluso usato)
const VetrinaHeroImage = ({ marca, modello, tipo }) => {
  const [imageUrl, setImageUrl] = React.useState(null);
  React.useEffect(() => {
    if (!marca || !modello || tipo === 'usato') return;
    let cancelled = false;
    axios.get(`${API}/public/drive-images/match?marca=${encodeURIComponent(marca)}&modello=${encodeURIComponent(modello)}`)
      .then(res => { if (!cancelled && res.data.found) setImageUrl(res.data.thumbnail_url); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [marca, modello, tipo]);
  if (!imageUrl) return null;
  return (
    <div className="w-full h-36 bg-gradient-to-b from-white to-slate-50 flex items-center justify-center px-6 py-3 -mt-2 mb-2">
      <img src={imageUrl} alt={`${marca} ${modello}`} className="max-w-full max-h-full object-contain drop-shadow-md" loading="lazy" onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
    </div>
  );
};

// Card USATO con embed Nextlane (layout orizzontale: info sx + player dx)
const UsatoHeroCard = ({ offer, onContact }) => {
  const isSold = offer.is_sold === true;
  const isOpzionato = offer.opzionato_ae || offer.opzionato_af || offer.opzionato_aj;
  const opzionatoLevel = offer.opzionato_ae && offer.opzionato_af ? 2 : (offer.opzionato_ae || offer.opzionato_aj ? 1 : 0);
  const [showTooltip, setShowTooltip] = React.useState(false);
  const hasNextlane = offer.link_immagine && offer.link_immagine.includes('nextlane.com/public/player');

  return (
    <Card className={`overflow-hidden transition-all duration-300 border-2 relative ${
      isSold 
        ? 'border-red-300 opacity-75 grayscale-[30%]' 
        : isOpzionato
          ? 'border-amber-400 hover:shadow-lg hover:border-amber-500'
          : 'border-emerald-200 hover:shadow-lg hover:border-emerald-400'
    }`}>
      {/* TIMBRO VENDUTA */}
      {isSold && (
        <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
          <div className="bg-red-600/95 text-white px-6 py-3 rounded-lg shadow-2xl transform -rotate-12 border-4 border-red-800">
            <div className="text-2xl font-black tracking-wider text-center">VENDUTA</div>
            <div className="text-xs text-red-200 text-center mt-1">Non più disponibile</div>
          </div>
        </div>
      )}

      {/* BADGE OPZIONATO */}
      {isOpzionato && !isSold && (
        <div className="absolute top-2 right-2 z-20 cursor-pointer"
          onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)}>
          <div className="flex items-center gap-1 bg-amber-100 border-2 border-amber-400 rounded-lg px-2 py-1 shadow-md">
            <span className="text-lg">🚩</span>
            {opzionatoLevel === 2 && <span className="text-lg">🚩</span>}
            <span className="text-xs font-bold text-amber-700 ml-1">OPZIONATA</span>
          </div>
          {showTooltip && (
            <div className="absolute top-full right-0 mt-2 w-72 bg-amber-50 border-2 border-amber-400 rounded-lg shadow-xl p-3 z-30">
              <div className="flex items-start gap-2">
                <Clock className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800 mb-1">⚠️ Auto già sotto l&apos;attenzione di un altro cliente</p>
                  <p className="text-xs text-amber-700 leading-relaxed">Velocizza la fase di affidamento inviando i documenti per poterti mettere in coda.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Layout orizzontale: Info SX + Player DX */}
      <div className="flex flex-col md:flex-row">
        {/* Colonna sinistra: Info veicolo */}
        <div className={`flex-1 p-5 flex flex-col justify-between ${isSold ? 'opacity-60' : ''}`}>
          <div>
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 border mb-2">USATO</Badge>
            <h3 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              {offer.marca} {offer.modello}
            </h3>
            <p className="text-sm text-slate-500 mt-1 line-clamp-2">{offer.versione}</p>

            <div className="grid grid-cols-2 gap-2 mt-4 text-sm">
              <div className="flex items-center gap-2 text-slate-600">
                <Calendar className="w-3.5 h-3.5 text-slate-400" />
                <span>{offer.durata_mesi} mesi</span>
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <Gauge className="w-3.5 h-3.5 text-slate-400" />
                <span>{offer.km_annui?.toLocaleString('it-IT')} km/a</span>
              </div>
              {offer.alimentazione && (
                <div className="text-slate-600 text-xs">⛽ {offer.alimentazione}</div>
              )}
              {offer.km_percorsi && (
                <div className="text-emerald-600 text-xs">📍 {offer.km_percorsi?.toLocaleString('it-IT')} km</div>
              )}
            </div>
          </div>

          {/* Pricing + CTA */}
          <div className="mt-4">
            <div className="p-3 bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-xl border border-emerald-200">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-slate-600">Anticipo</span>
                <span className="text-sm font-semibold text-slate-800">€{offer.anticipo?.toLocaleString('it-IT')} <span className="text-[10px] text-slate-500">IVA Escl.</span></span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-600">Canone</span>
                <div className="text-right">
                  <span className={`text-2xl font-bold ${isSold ? 'text-slate-400 line-through' : 'text-emerald-700'}`}>
                    €{offer.canone_netto?.toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </span>
                  <span className="text-xs text-slate-500">/mese</span>
                </div>
              </div>
            </div>
            <Button
              className={`w-full gap-2 mt-3 ${isSold ? 'bg-slate-400 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700'}`}
              onClick={() => !isSold && onContact(offer)}
              disabled={isSold}
              data-testid={`vetrina-contact-usato-${offer.marca}-${offer.modello}`}
            >
              {isSold ? 'Non Disponibile' : <><Phone className="w-4 h-4" /> Richiedi Informazioni</>}
            </Button>
          </div>
        </div>

        {/* Colonna destra: Player Nextlane */}
        {hasNextlane && (
          <div className="md:w-[55%] bg-slate-900 flex items-center justify-center min-h-[280px] md:min-h-0">
            <iframe
              src={offer.link_immagine}
              title={`${offer.marca} ${offer.modello} - Vista 360°`}
              className="w-full h-full min-h-[280px]"
              style={{ border: 'none' }}
              allow="autoplay; fullscreen"
              loading="lazy"
            />
          </div>
        )}
      </div>
    </Card>
  );
};

// Vehicle Card Component
const VehicleCard = ({ offer, onContact }) => {
  const tipoColors = {
    nuovo: 'bg-blue-100 text-blue-700 border-blue-200',
    '4vantage': 'bg-violet-100 text-violet-700 border-violet-200',
    usato: 'bg-emerald-100 text-emerald-700 border-emerald-200'
  };

  const tipoLabels = {
    nuovo: 'NUOVO',
    '4vantage': '4VANTAGE',
    usato: 'USATO'
  };

  // Check if vehicle is sold (show red stamp)
  const isSold = offer.is_sold === true;
  
  // Check OPZIONATO status (USATO: AE/AF, NUOVO/4VANTAGE: AJ)
  const isOpzionato = offer.opzionato_ae || offer.opzionato_af || offer.opzionato_aj;
  const opzionatoLevel = offer.opzionato_ae && offer.opzionato_af ? 2 : (offer.opzionato_ae || offer.opzionato_aj ? 1 : 0);
  
  // Stato per tooltip
  const [showTooltip, setShowTooltip] = React.useState(false);

  return (
    <Card className={`overflow-hidden transition-all duration-300 border-2 relative ${
      isSold 
        ? 'border-red-300 opacity-75 grayscale-[30%]' 
        : isOpzionato
          ? 'border-amber-400 hover:shadow-lg hover:border-amber-500'
          : 'hover:shadow-lg hover:border-primary/30'
    }`}>
      {/* TIMBRO VENDUTA - Overlay d'impatto */}
      {isSold && (
        <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
          <div className="bg-red-600/95 text-white px-6 py-3 rounded-lg shadow-2xl transform -rotate-12 border-4 border-red-800">
            <div className="text-2xl font-black tracking-wider text-center">
              VENDUTA
            </div>
            <div className="text-xs text-red-200 text-center mt-1">
              Non più disponibile
            </div>
          </div>
        </div>
      )}
      
      {/* BADGE OPZIONATO - Bandierine gialle */}
      {isOpzionato && !isSold && (
        <div 
          className="absolute top-2 right-2 z-20 cursor-pointer"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <div className="flex items-center gap-1 bg-amber-100 border-2 border-amber-400 rounded-lg px-2 py-1 shadow-md">
            {/* Bandierine gialle */}
            <span className="text-lg">🚩</span>
            {opzionatoLevel === 2 && <span className="text-lg">🚩</span>}
            <span className="text-xs font-bold text-amber-700 ml-1">OPZIONATA</span>
          </div>
          
          {/* Tooltip con messaggio di urgenza */}
          {showTooltip && (
            <div className="absolute top-full right-0 mt-2 w-72 bg-amber-50 border-2 border-amber-400 rounded-lg shadow-xl p-3 z-30">
              <div className="flex items-start gap-2">
                <div className="text-amber-600 mt-0.5">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-amber-800 mb-1">
                    ⚠️ Auto già sotto l&apos;attenzione di un altro cliente
                  </p>
                  <p className="text-xs text-amber-700 leading-relaxed">
                    Velocizza la fase di affidamento inviando i documenti per poterti mettere in coda e concorrere all&apos;assegnazione.
                  </p>
                  <p className="text-xs text-amber-600 mt-2 font-medium">
                    💡 L&apos;opzione e l&apos;ordine sono subordinati all&apos;approvazione del credito.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      <CardHeader className={`pb-3 bg-gradient-to-r from-slate-50 to-white ${isSold ? 'opacity-60' : ''}`}>
        {/* Hero Image */}
        <VetrinaHeroImage marca={offer.marca} modello={offer.modello} tipo={offer.tipo} />
        
        <div className="flex items-start justify-between">
          <div>
            <Badge className={`${tipoColors[offer.tipo]} border mb-2`}>
              {tipoLabels[offer.tipo]}
            </Badge>
            <CardTitle className="text-lg font-bold text-slate-800">
              {offer.marca} {offer.modello}
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1 line-clamp-2">{offer.versione}</p>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className={`pt-4 space-y-4 ${isSold ? 'opacity-60' : ''}`}>
        {/* Details */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="flex items-center gap-2 text-slate-600">
            <Calendar className="w-4 h-4 text-slate-400" />
            <span>{offer.durata_mesi} mesi</span>
          </div>
          <div className="flex items-center gap-2 text-slate-600">
            <Gauge className="w-4 h-4 text-slate-400" />
            <span>{offer.km_annui?.toLocaleString('it-IT')} km/anno</span>
          </div>
          {offer.alimentazione && (
            <div className="col-span-2 text-slate-600">
              ⛽ {offer.alimentazione}
            </div>
          )}
          {offer.tipo === 'usato' && offer.km_percorsi && (
            <div className="col-span-2 text-emerald-600 text-xs">
              📍 {offer.km_percorsi?.toLocaleString('it-IT')} km percorsi
              {offer.targa && ` • Targa: ${offer.targa}`}
            </div>
          )}
        </div>

        {/* Pricing */}
        <div className="p-4 bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl border border-primary/20">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-600">Anticipo</span>
            <span className="font-semibold text-slate-800">
              €{offer.anticipo?.toLocaleString('it-IT')} <span className="text-xs text-slate-500">IVA Escl.</span>
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">Canone mensile</span>
            <div className="text-right">
              <span className={`text-2xl font-bold ${isSold ? 'text-slate-400 line-through' : 'text-primary'}`}>
                €{offer.canone_netto?.toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </span>
              <span className="text-sm text-slate-500">/mese</span>
              <p className="text-xs text-slate-400">IVA Esclusa</p>
            </div>
          </div>
        </div>

        {/* CTA Button - Disabled if sold */}
        <Button 
          className={`w-full gap-2 ${isSold ? 'bg-slate-400 cursor-not-allowed' : 'bg-primary hover:bg-primary/90'}`}
          onClick={() => !isSold && onContact(offer)}
          disabled={isSold}
        >
          {isSold ? (
            <>
              <span className="line-through">Non Disponibile</span>
            </>
          ) : (
            <>
              <Phone className="w-4 h-4" />
              Richiedi Informazioni
              <ChevronRight className="w-4 h-4" />
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

// Section Header Component
const SectionHeader = ({ title, subtitle, icon: Icon, color }) => (
  <div className="flex items-center gap-4 mb-6">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
      <Icon className="w-6 h-6 text-white" />
    </div>
    <div>
      <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans' }}>
        {title}
      </h2>
      <p className="text-sm text-slate-500">{subtitle}</p>
    </div>
  </div>
);


const VetrinaPage = () => {
  const [vetrina, setVetrina] = useState({ nuovo: [], '4vantage': [], usato: [], parametri: {} });
  const [loading, setLoading] = useState(true);
  const [contactDialog, setContactDialog] = useState({ open: false, offer: null });
  const [submitting, setSubmitting] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  
  // Form Prospect completo + Password
  const [formData, setFormData] = useState({
    // Dati base
    nome_cognome: '',
    telefono: '',
    email: '',
    // Password per area riservata
    password: '',
    conferma_password: '',
    // Dati Prospect completi
    tipologia_cliente: '',
    codice_fiscale: '',
    partita_iva: '',
    indirizzo: '',
    citta: '',
    cap: '',
    responsabile: '',
    km_annui_previsti: '',
    messaggio: '',
    // Fase 1.2: Codice Convenzione opzionale
    codice_convenzione: ''
  });
  
  // Privacy checkbox e popup
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [giaClienteAyvens, setGiaClienteAyvens] = useState(null);
  const [showPrivacyPopup, setShowPrivacyPopup] = useState(false);

  useEffect(() => {
    fetchVetrina();
  }, []);

  const fetchVetrina = async () => {
    try {
      const response = await axios.get(`${API}/public/vetrina`);
      setVetrina(response.data);
    } catch (error) {
      console.error('Error fetching vetrina:', error);
      toast.error('Errore nel caricamento delle offerte');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenContact = (offer) => {
    setContactDialog({ open: true, offer });
    setRegistrationSuccess(false);
    setPrivacyAccepted(false);  // Reset privacy checkbox
    setGiaClienteAyvens(null);
    setFormData({
      nome_cognome: '',
      telefono: '',
      email: '',
      password: '',
      conferma_password: '',
      tipologia_cliente: '',
      codice_fiscale: '',
      partita_iva: '',
      indirizzo: '',
      citta: '',
      cap: '',
      responsabile: '',
      km_annui_previsti: '',
      messaggio: ''
    });
  };

  const handleSubmitContact = async (e) => {
    e.preventDefault();

    // Validazione campi obbligatori
    if (!formData.nome_cognome || !formData.email || !formData.telefono) {
      toast.error('Compila tutti i campi obbligatori (Nome, Email, Telefono)');
      return;
    }

    // Validazione Password (min 8)
    if (!formData.password || formData.password.length < 8) {
      toast.error('La password deve avere almeno 8 caratteri');
      return;
    }
    if (formData.password !== formData.conferma_password) {
      toast.error('Le password non coincidono');
      return;
    }

    // Validazione Privacy Policy
    if (!privacyAccepted) {
      toast.error('Devi accettare la Privacy Policy per procedere');
      return;
    }
    if (giaClienteAyvens === null) {
      toast.error('Indica se sei già cliente Ayvens / ALD Automotive');
      return;
    }

    // Validazione email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Inserisci un indirizzo email valido');
      return;
    }

    setSubmitting(true);
    try {
      const offerParams = contactDialog.offer ? {
        vehicle_id: contactDialog.offer.id,
        anticipo: contactDialog.offer.anticipo,
        durata_mesi: contactDialog.offer.durata_mesi,
        km_annui: contactDialog.offer.km_annui,
        canone_netto: contactDialog.offer.canone_netto,
        canone_base: contactDialog.offer.canone_base,
        tipo_veicolo: contactDialog.offer.tipo
      } : {};

      const response = await axios.post(`${API}/public/richiesta-info`, {
        nome_cognome: formData.nome_cognome,
        telefono: formData.telefono,
        email: formData.email,
        password: formData.password,
        conferma_password: formData.conferma_password,
        messaggio: formData.messaggio,
        veicolo_interesse: contactDialog.offer
          ? `${contactDialog.offer.marca} ${contactDialog.offer.modello} - €${contactDialog.offer.canone_netto}/mese`
          : null,
        codice_convenzione: (formData.codice_convenzione || '').toUpperCase() || undefined,
        accept_privacy: privacyAccepted,
        gia_cliente_ayvens: giaClienteAyvens,
        ...offerParams
      });

      if (response.data?.pending) {
        // Convenzione flow → status PENDING, niente auto-login
        setRegistrationSuccess(true);
        toast.success(response.data.message || 'Iscrizione ricevuta! In attesa di verifica.');
        if (response.data.ayvens_message) toast.info(response.data.ayvens_message, { duration: 10000 });
      } else if (response.data?.success && response.data?.access_token) {
        // Auto-login: il cookie di sessione è già impostato dal server
        if (response.data.ayvens_message) toast.info(response.data.ayvens_message, { duration: 8000 });
        toast.success('Benvenuto! Ti stiamo portando nella tua area riservata...');
        // Full page navigation per re-inizializzare AuthContext con la sessione appena creata
        setTimeout(() => { window.location.href = '/dashboard/offerte'; }, response.data.ayvens_message ? 5000 : 500);
      } else {
        toast.error('Si è verificato un errore. Riprova.');
      }
    } catch (error) {
      console.error('Errore invio richiesta:', error);
      const errorMsg = error.response?.data?.detail;
      if (Array.isArray(errorMsg)) {
        toast.error('Verifica i dati inseriti: ' + errorMsg.map(e => e.msg).join(', '));
      } else if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else {
        toast.error('Errore durante l\'invio. Verifica i dati e riprova.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const totalOffers = vetrina.nuovo.length + vetrina['4vantage'].length + vetrina.usato.length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header - Minimal without logo */}
      <header className="bg-white/95 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-end h-14 gap-4">
            <a 
              href="/login" 
              className="text-sm text-slate-600 hover:text-primary transition-colors"
            >
              Area Riservata
            </a>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setContactDialog({ open: true, offer: null })}
            >
              <Phone className="w-4 h-4 mr-2" />
              Contattaci
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section - New Design */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 text-white overflow-hidden min-h-[420px] flex items-center">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1587572345949-2878f2aaa370?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwzfHxvcGVuJTIwcm9hZCUyMGJsdWUlMjBza3klMjBob3Jpem9uJTIwZHJpdmluZyUyMHBlcnNwZWN0aXZlJTIwZnJlZWRvbXxlbnwwfHx8fDE3Njg4MDk3OTh8MA&ixlib=rb-4.1.0&q=85")',
          }}
        />
        {/* Overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40" />
        
        <div className="max-w-7xl mx-auto relative w-full">
          <div className="text-center max-w-3xl mx-auto">
            {/* Centered Logo - Versione Light per sfondo scuro */}
            <div className="flex justify-center mb-6">
              <img 
                src="/assets/logos/logo-light.png?v=2026" 
                alt="MAcheNoleggio" 
                className="h-28 w-auto drop-shadow-2xl"
                style={{ filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.3))' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
            
            {/* Title */}
            <h1 
              className="text-4xl sm:text-5xl font-bold mb-3 drop-shadow-lg" 
              style={{ 
                fontFamily: 'Plus Jakarta Sans',
                textShadow: '2px 2px 8px rgba(0,0,0,0.5)'
              }}
            >
              Noleggio a Lungo Termine
            </h1>
            
            {/* Subtitle */}
            <p 
              className="text-xl sm:text-2xl font-medium mb-6 drop-shadow-md"
              style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.5)' }}
            >
              Alcune offerte
            </p>
            
            {/* Marketing CTA Text */}
            <p 
              className="text-base sm:text-lg text-white/95 mb-8 max-w-2xl mx-auto leading-relaxed drop-shadow-md"
              style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.4)' }}
            >
              Il nostro catalogo è in continuo aggiornamento. Registrati per essere ricontattato da un professionista 
              e ricevere la tua quotazione riservata.
            </p>
            
            {/* Benefits Icons - Minimal White */}
            <div className="flex flex-wrap justify-center gap-8 sm:gap-12">
              <div className="flex items-center gap-2">
                <Car className="w-5 h-5 text-white drop-shadow-md" />
                <span className="text-sm font-medium drop-shadow-md">Marchi diversificati</span>
              </div>
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-white drop-shadow-md" />
                <span className="text-sm font-medium drop-shadow-md">Contratti flessibili</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-white drop-shadow-md" />
                <span className="text-sm font-medium drop-shadow-md">Tutto incluso</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <span className="ml-3 text-slate-600">Caricamento offerte...</span>
          </div>
        ) : totalOffers === 0 ? (
          <Card className="max-w-lg mx-auto">
            <CardContent className="py-12 text-center">
              <Car className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Nessuna offerta disponibile</h3>
              <p className="text-slate-500 mb-4">
                Le offerte verranno caricate a breve. Contattaci per un preventivo personalizzato!
              </p>
              <Button onClick={() => setContactDialog({ open: true, offer: null })}>
                <Phone className="w-4 h-4 mr-2" />
                Richiedi Preventivo
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-12">
            {/* Auto Nuove - Layout 2x3 */}
            {vetrina.nuovo.length > 0 && (
              <section>
                <SectionHeader 
                  title="Auto Nuove" 
                  subtitle={`${vetrina.nuovo.length} marchi diversi • 60 mesi • 10.000 km/anno`}
                  icon={Car}
                  color="bg-blue-500"
                />
                {/* Griglia 2x3: 3 colonne su desktop, 2 su tablet, 1 su mobile */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {vetrina.nuovo.map((offer, idx) => (
                    <VehicleCard key={`nuovo-${idx}`} offer={offer} onContact={handleOpenContact} />
                  ))}
                </div>
              </section>
            )}

            {/* 4Vantage - Layout 1x3 */}
            {vetrina['4vantage'].length > 0 && (
              <section>
                <SectionHeader 
                  title="Programma 4Vantage" 
                  subtitle="Dipendenti e P.IVA forfettarie • 48 mesi • 30.000 km/anno"
                  icon={Sparkles}
                  color="bg-violet-500"
                />
                {/* Griglia 1x3: 3 colonne su desktop, centrato se meno di 3 elementi */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center">
                  {vetrina['4vantage'].map((offer, idx) => (
                    <VehicleCard key={`4vantage-${idx}`} offer={offer} onContact={handleOpenContact} />
                  ))}
                </div>
              </section>
            )}

            {/* Auto Usate */}
            {vetrina.usato.length > 0 && (
              <section>
                <SectionHeader 
                  title="Auto Usate Selezionate" 
                  subtitle={`${vetrina.usato.length} veicoli garantiti • 60 mesi • 10.000 km/anno`}
                  icon={Shield}
                  color="bg-emerald-500"
                />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {vetrina.usato.map((offer, idx) => (
                    <UsatoHeroCard key={`usato-${idx}`} offer={offer} onContact={handleOpenContact} />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}

        {/* CTA Section */}
        <section className="mt-16 p-8 bg-gradient-to-r from-primary/10 to-violet-100 rounded-2xl border border-primary/20">
          <div className="max-w-2xl mx-auto text-center">
            <h3 className="text-2xl font-bold text-slate-800 mb-3" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Non trovi quello che cerchi?
            </h3>
            <p className="text-slate-600 mb-6">
              Personalizza la tua offerta! Compila il modulo Prospect e ricevi un preventivo su misura per le tue esigenze.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                onClick={() => setContactDialog({ open: true, offer: null })}
                className="gap-2"
              >
                <MessageSquare className="w-5 h-5" />
                Richiedi Preventivo Personalizzato
              </Button>
              <a href="/login">
                <Button variant="outline" size="lg" className="gap-2 w-full sm:w-auto">
                  <ExternalLink className="w-5 h-5" />
                  Accedi all&apos;Area Riservata
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Fiscal Note */}
        <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800 text-center">
            <strong>Nota fiscale:</strong> Tutti gli importi indicati (Canoni, Anticipi e Prezzi di Listino) sono <strong>IVA ESCLUSA (22%)</strong>. 
            I valori mostrati provengono direttamente dai listini ufficiali e non includono l&apos;imposta sul valore aggiunto.
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h4 className="font-bold text-lg mb-4">MAAC FINANCE SRL</h4>
              <p className="text-slate-400 text-sm">
                Noleggio a lungo termine per privati e aziende.
                <br />Soluzioni personalizzate per ogni esigenza.
              </p>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">Contatti</h4>
              <div className="space-y-2 text-slate-400 text-sm">
                <p className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  preventivi@machenoleggio.it
                </p>
                <p className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Contattaci per info
                </p>
              </div>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">Note Legali</h4>
              <p className="text-slate-400 text-sm">
                Tutti i prezzi sono IVA esclusa (22%).
                <br />Le offerte sono soggette a disponibilità.
              </p>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-slate-500 text-sm">
            <p>MAcheNoleggio è un marchio di proprietà di MAAC FINANCE SRL</p>
            <p className="mt-2">© {new Date().getFullYear()} Tutti i diritti riservati.</p>
          </div>
        </div>
      </footer>

      {/* Contact Dialog - Richiesta Info (Solo SMART) */}
      <Dialog open={contactDialog.open} onOpenChange={(open) => setContactDialog({ open, offer: contactDialog.offer })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>
              {registrationSuccess ? 'Richiesta Inviata!' : 'Richiedi Informazioni'}
            </DialogTitle>
          </DialogHeader>
          
          {registrationSuccess ? (
            // Success State - Iscrizione via convenzione in attesa di verifica
            <div className="py-6 text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800">Iscrizione ricevuta!</h3>
              <p className="text-slate-600">
                Il tuo account è in fase di verifica.
              </p>
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-amber-800 font-medium">
                  Riceverai un'email non appena il tuo profilo sarà abilitato dall'amministrazione.
                </p>
              </div>
              <div className="pt-4">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setContactDialog({ open: false, offer: null });
                    setRegistrationSuccess(false);
                  }}
                >
                  Chiudi
                </Button>
              </div>
            </div>
          ) : (
            // Form SMART Semplificato - Solo Nome, Telefono, Email
            <form onSubmit={handleSubmitContact} className="space-y-4 py-4">
              {contactDialog.offer && (
                <div className="p-3 bg-primary/5 rounded-lg border border-primary/20 mb-4">
                  <p className="text-sm text-slate-600">Veicolo di interesse:</p>
                  <p className="font-semibold text-slate-800">
                    {contactDialog.offer.marca} {contactDialog.offer.modello}
                  </p>
                  <p className="text-sm text-primary">
                    €{contactDialog.offer.canone_netto?.toLocaleString('it-IT')}/mese IVA Escl.
                  </p>
                </div>
              )}

              {/* Form SMART - Solo campi essenziali */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="nome_cognome">Nome e Cognome / Ragione Sociale *</Label>
                  <Input
                    id="nome_cognome"
                    data-testid="vetrina-form-nome"
                    value={formData.nome_cognome}
                    onChange={(e) => setFormData({...formData, nome_cognome: e.target.value})}
                    placeholder="Es. Mario Rossi / Rossi SRL"
                    autoComplete="name"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="telefono">Telefono *</Label>
                  <Input
                    id="telefono"
                    data-testid="vetrina-form-telefono"
                    value={formData.telefono}
                    onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                    placeholder="333 1234567"
                    autoComplete="tel"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="vetrina-form-email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="esempio@email.it"
                    autoComplete="off"
                    required
                  />
                </div>

                {/* Credenziali (Fase 2 Task 2.5): password + conferma */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="password">Password * <span className="text-xs text-slate-400 font-normal">(min 8 caratteri)</span></Label>
                    <Input
                      id="password"
                      type="password"
                      data-testid="vetrina-form-password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      placeholder="Almeno 8 caratteri"
                      autoComplete="new-password"
                      minLength={8}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="conferma_password">Conferma Password *</Label>
                    <Input
                      id="conferma_password"
                      type="password"
                      data-testid="vetrina-form-conferma-password"
                      value={formData.conferma_password}
                      onChange={(e) => setFormData({...formData, conferma_password: e.target.value})}
                      placeholder="Ripeti password"
                      autoComplete="new-password"
                      minLength={8}
                      required
                    />
                  </div>
                </div>

                <AyvensQuestion value={giaClienteAyvens} onChange={setGiaClienteAyvens} testIdPrefix="vetrina-form-ayvens" />

                {/* Messaggio opzionale */}
                <div className="space-y-2">
                  <Label htmlFor="messaggio">Note o richieste particolari</Label>
                  <textarea
                    id="messaggio"
                    data-testid="vetrina-form-messaggio"
                    value={formData.messaggio}
                    onChange={(e) => setFormData({...formData, messaggio: e.target.value})}
                    className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    placeholder="Eventuali richieste o domande..."
                    rows={2}
                  />
                </div>

                {/* Codice Convenzione opzionale */}
                <div className="space-y-2">
                  <Label htmlFor="codice_convenzione">Codice Convenzione (opzionale)</Label>
                  <Input
                    id="codice_convenzione"
                    data-testid="vetrina-form-codice-convenzione"
                    value={formData.codice_convenzione}
                    onChange={(e) => setFormData({...formData, codice_convenzione: e.target.value.toUpperCase()})}
                    placeholder="Inserisci codice"
                  />
                </div>
              </div>

              {/* Info box */}
              <div className="text-xs text-slate-500 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="font-medium text-blue-700 mb-1">Cosa succede dopo?</p>
                <p className="text-blue-600">
                  Un nostro consulente analizzerà la tua richiesta e ti contatterà per proporti la soluzione migliore.
                </p>
              </div>

              {/* Privacy Checkbox - Obbligatorio */}
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <input
                  type="checkbox"
                  id="privacy-checkbox"
                  data-testid="privacy-checkbox"
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                />
                <label htmlFor="privacy-checkbox" className="text-sm text-slate-600">
                  Dichiaro di aver letto e accettato la{' '}
                  <button
                    type="button"
                    onClick={() => setShowPrivacyPopup(true)}
                    className="text-primary hover:underline font-medium"
                  >
                    Privacy Policy
                  </button>
                  {' '}*
                </label>
              </div>

              <div className="flex gap-3 pt-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setContactDialog({ open: false, offer: null })}
                >
                  Annulla
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1"
                  data-testid="vetrina-form-submit"
                  disabled={submitting || !privacyAccepted}
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Invio...</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" />Invia Richiesta</>
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Privacy Policy Popup */}
      <Dialog open={showPrivacyPopup} onOpenChange={setShowPrivacyPopup}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              Informativa sulla Privacy
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm text-slate-600">
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h3 className="font-semibold text-slate-800 mb-2">Titolare del Trattamento</h3>
              <p>MAAC FINANCE SRL</p>
              <p>Email: privacy@machenoleggio.it</p>
            </div>
            
            <div>
              <h3 className="font-semibold text-slate-800 mb-2">Finalità del Trattamento</h3>
              <p>
                I dati personali forniti (nome, cognome, email, telefono) saranno trattati esclusivamente 
                per le seguenti finalità:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Rispondere alla richiesta di contatto</li>
                <li>Fornire informazioni commerciali sui servizi di noleggio a lungo termine</li>
                <li>Elaborare preventivi personalizzati</li>
                <li>Gestire l'eventuale pratica di noleggio</li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-slate-800 mb-2">Base Giuridica</h3>
              <p>
                Il trattamento dei dati si basa sul consenso dell'interessato (art. 6, par. 1, lett. a del GDPR) 
                e sull'esecuzione di misure precontrattuali adottate su richiesta dell'interessato (art. 6, par. 1, lett. b del GDPR).
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-slate-800 mb-2">Cessione a Terzi</h3>
              <p>
                I dati <strong>non saranno ceduti a terzi</strong> non strettamente legati alla pratica di noleggio. 
                Potranno essere condivisi esclusivamente con:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Società di noleggio partner per l'evasione della richiesta</li>
                <li>Istituti finanziari per la valutazione creditizia (solo in caso di richiesta di noleggio)</li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-slate-800 mb-2">Conservazione dei Dati</h3>
              <p>
                I dati saranno conservati per il tempo necessario a evadere la richiesta e, successivamente, 
                per il periodo previsto dagli obblighi di legge.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-slate-800 mb-2">Diritti dell'Interessato</h3>
              <p>
                È possibile esercitare i diritti previsti dal GDPR (accesso, rettifica, cancellazione, 
                limitazione, portabilità, opposizione) contattando il Titolare all'indirizzo email indicato.
              </p>
            </div>
            
            <div className="pt-4 border-t border-slate-200">
              <p className="text-xs text-slate-500 italic">
                Ultimo aggiornamento: Gennaio 2026
              </p>
            </div>
          </div>
          <div className="flex justify-end pt-4">
            <Button onClick={() => setShowPrivacyPopup(false)}>
              Ho compreso
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VetrinaPage;
